import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiPlay, FiLoader, FiAlertCircle, FiX } from "react-icons/fi";
import { useAuditRunner } from "../hooks/useAuditRunner";

const AuditForm = () => {
  const [url, setUrl] = useState("");
  const [localError, setLocalError] = useState("");
  const [auditMode, setAuditMode] = useState("single");

  const { runAudit, cancelAudit, status, progress, error, isRunning } =
    useAuditRunner();

  const validateUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!url.trim()) {
      setLocalError("URL is required");
      return;
    }

    if (!validateUrl(url)) {
      setLocalError("Enter a valid URL with http:// or https://");
      return;
    }

    await runAudit(url, auditMode);
  };

  const handleCancel = () => {
    cancelAudit();
  };

  const getStatusMessage = () => {
    if (progress < 30) return "Initializing audit...";
    if (progress < 50) return "Running Lighthouse analysis...";
    if (progress < 80) return "Analyzing JavaScript bundles...";
    return "Finalizing report...";
  };

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-heading-03 text-gray-100 mb-3 font-ibm">
          Enter your React application URL
        </h3>
        <p className="text-body-02 text-gray-70">
          We'll analyze your deployed application's performance metrics and
          JavaScript bundle composition with detailed optimization
          recommendations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="url"
            className="text-label-01 text-gray-70 uppercase tracking-wide mb-2 block"
          >
            Website URL *
          </label>
          <input
            type="url"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={isRunning}
            className="w-full border-none border-b-2 border-gray-40 bg-gray-10 px-4 py-3 text-body-02 text-gray-100 placeholder-gray-50 focus:border-blue-600 focus:bg-gray-10 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="https://your-react-app.com"
            required
          />
        </div>

        <div className="mb-4">
          <label className="text-label-01 text-gray-70 uppercase tracking-wide mb-2 block">
            Audit Mode
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="single"
                checked={auditMode === "single"}
                onChange={(e) => setAuditMode(e.target.value)}
                className="text-blue-600"
              />
              <span className="text-body-01 text-gray-100">
                Single Run (Fast, ~15s)
              </span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="multiple"
                checked={auditMode === "multiple"}
                onChange={(e) => setAuditMode(e.target.value)}
                className="text-blue-600"
              />
              <span className="text-body-01 text-gray-100">
                Multiple Runs (Accurate, ~45s)
              </span>
            </label>
          </div>
          <p className="text-label-01 text-gray-60 mt-1">
            Multiple runs provide more consistent scores but take longer
          </p>
        </div>

        {localError ||
          (error && (
            <div className="flex items-center space-x-2 mt-2 text-body-01 text-red-60">
              <FiAlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{localError || error}</span>
            </div>
          ))}

        {isRunning && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="animate-spin">
                  <FiLoader className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-body-02 text-gray-100 font-medium">
                    {getStatusMessage()}
                  </p>
                  <p className="text-body-01 text-gray-70">
                    This may take up to 30 seconds...
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-heading-02 text-blue-600 font-ibm">
                  {progress}%
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="w-full bg-gray-20 h-3 rounded-full overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-600 to-blue-500 h-full transition-all duration-500 ease-out rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-60 mt-2">
                <span>Starting</span>
                <span>Analyzing</span>
                <span>Complete</span>
              </div>
            </div>

            <div className="bg-gray-20 p-4 rounded border-l-4 border-blue-600">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div
                  className={`${progress >= 20 ? "text-blue-600" : "text-gray-50"}`}
                >
                  <div className="flex items-center justify-center mb-1">
                    {progress >= 20 ? (
                      <FiPlay className="h-4 w-4" />
                    ) : (
                      <div className="w-4 h-4 border border-gray-40 rounded-full" />
                    )}
                  </div>
                  <span className="text-label-01 uppercase tracking-wide">
                    Lighthouse
                  </span>
                </div>
                <div
                  className={`${progress >= 60 ? "text-blue-600" : "text-gray-50"}`}
                >
                  <div className="flex items-center justify-center mb-1">
                    {progress >= 60 ? (
                      <FiPlay className="h-4 w-4" />
                    ) : (
                      <div className="w-4 h-4 border border-gray-40 rounded-full" />
                    )}
                  </div>
                  <span className="text-label-01 uppercase tracking-wide">
                    Analysis
                  </span>
                </div>
                <div
                  className={`${progress >= 90 ? "text-blue-600" : "text-gray-50"}`}
                >
                  <div className="flex items-center justify-center mb-1">
                    {progress >= 90 ? (
                      <FiPlay className="h-4 w-4" />
                    ) : (
                      <div className="w-4 h-4 border border-gray-40 rounded-full" />
                    )}
                  </div>
                  <span className="text-label-01 uppercase tracking-wide">
                    Report
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-4">
            {!isRunning ? (
              <button
                type="submit"
                className="btn-primary flex items-center space-x-2 min-h-12"
              >
                <FiPlay className="h-4 w-4" />
                <span>Start Performance Audit</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCancel}
                className="btn-secondary flex items-center space-x-2 min-h-12"
              >
                <FiX className="h-4 w-4" />
                <span>Cancel Audit</span>
              </button>
            )}
          </div>

          {!isRunning && (
            <div className="text-body-01 text-gray-60">
              <span className="hidden sm:inline">Powered by Lighthouse</span>
            </div>
          )}
        </div>

        {!isRunning && (
          <div className="bg-gray-10 p-4 rounded border border-gray-30">
            <h4 className="text-heading-02 text-gray-100 font-ibm mb-2">
              What we'll analyze:
            </h4>
            <ul className="space-y-1 text-body-01 text-gray-70">
              <li>• Core Web Vitals (FCP, LCP, TTI, TBT, CLS)</li>
              <li>• Performance score and detailed metrics</li>
              <li>• JavaScript bundle size and composition</li>
              <li>• Framework and library detection</li>
              <li>• Actionable optimization recommendations</li>
            </ul>
          </div>
        )}
      </form>
    </div>
  );
};

export default AuditForm;
